import { Brush, Close as CloseIcon } from '@mui/icons-material';
import { Divider, Drawer, IconButton } from '@mui/material';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';

import { ITaskInfo } from '@/entities/task/model/client';
import { useYaMetrika } from '@/entities/yaMetrika';
import { ChangeQueueColorModal } from '@/features/changeQueueColor';
import { colorStore } from '@/features/queueColors';
import { useTaskStore } from '@/features/Task';
import { WeeklyWorkLog } from '@/types';
import { AnalyticsEvent } from '@/types/yametrika';
import { getTrackerUrl } from '@/utils/getTrackerUrl';

import { getFieldsConfig } from './config/fields';
import { DetailsContent } from './ui/DetailsContent';
import { TaskLoading } from './ui/TaskLoading';
import { getProcessedFields } from './utils/processFields';

import s from './DetailsDrawer.module.scss';

type DetailsDrawerProps = {
  open: boolean;
  onClose: () => void;
  onExited?: () => void;
  log?: WeeklyWorkLog | null;
  task?: ITaskInfo | null;
};

export const DetailsDrawer: React.FC<DetailsDrawerProps> = observer(
  ({ open, onClose, onExited, log, task: taskProp }) => {
    const { sendEvent } = useYaMetrika();
    const { loadTask, task: taskFromStore, loadingStage } = useTaskStore();
    const [isColorModalOpen, setIsColorModalOpen] = useState(false);

    const task = taskProp || taskFromStore;

    useEffect(() => {
      if (open && log) {
        loadTask(log.taskKey);
      }

      if (open) {
        sendEvent(AnalyticsEvent.openDetailDrawer);
      }
    }, [open, log]);

    const fieldsConfig = useMemo(() => getFieldsConfig(), []);

    const timeFields = useMemo(
      () => getProcessedFields(fieldsConfig, log || null, task, 'time'),
      [fieldsConfig, log, task, colorStore.colorMapping],
    );

    const generalFields = useMemo(
      () => getProcessedFields(fieldsConfig, log || null, task, 'general'),
      [fieldsConfig, log, task, colorStore.colorMapping],
    );

    const taskKey = log?.taskKey || task?.key;
    const taskTitle = log?.taskTitle || task?.summary;
    const queue = taskKey?.split('-')[0];
    const needsBorder = queue ? colorStore.needsBorder(queue) : false;

    return (
      <>
        <Drawer
          anchor="right"
          open={open}
          onClose={onClose}
          transitionDuration={250}
          onTransitionExited={onExited}
          sx={{
            '.MuiPaper-root': {
              borderRadius: '20px 0 0 20px',
              boxShadow: 'none',
            },
          }}
        >
          <div className={s.drawer}>
            <div className={s.header}>
              <h1>Подробности задачи</h1>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </div>

            <Divider />

            {taskKey && taskTitle ? (
              <>
                <div className={s.row}>
                  <div className={s.key}>
                    <span>Ключ задачи</span>
                  </div>
                  <div className={clsx(s.value, s.taskKey)}>
                    <a
                      href={getTrackerUrl(taskKey)}
                      style={{
                        color: colorStore.getColorByPrefix(queue || ''),
                        ...(needsBorder && {
                          textShadow: '0 0 1px rgba(0, 0, 0, 0.5), 0 0 1px rgba(0, 0, 0, 0.5)',
                        }),
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {taskKey}
                    </a>
                    {queue && (
                      <IconButton
                        size="small"
                        onClick={() => setIsColorModalOpen(true)}
                        className={s.colorButton}
                        sx={{
                          padding: '4px',
                          marginLeft: '8px',
                          color: 'var(--secondary-color)',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <Brush fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                </div>

                <h2 className={s.taskTitle}>{taskTitle}</h2>
              </>
            ) : (
              <TaskLoading isLoading={true} />
            )}

            <DetailsContent
              isLoading={loadingStage.isLoading}
              log={log ?? undefined}
              task={task}
              timeFields={timeFields}
              generalFields={generalFields}
              loadingStage={loadingStage}
            />
          </div>
        </Drawer>

        {queue && (
          <ChangeQueueColorModal
            open={isColorModalOpen}
            onClose={() => setIsColorModalOpen(false)}
            queue={queue}
          />
        )}
      </>
    );
  },
);
